import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  FieldMessage,
  LiveRegion,
  MarkdownUploadArea,
  PageLayout,
  Select,
  Tabs,
  useLanguage,
  useToast,
  useTranslate,
  type MarkdownParseError,
  type MarkdownUploadState,
} from '@athena/ui'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { usePageTitle } from '../app/page-title.js'
import { AutomaticPracticeForm } from '../features/practices/AutomaticPracticeForm.js'
import { ChatGPTPromptPanel } from '../features/practices/ChatGPTPromptPanel.js'
import { CreateFailure } from '../features/practices/CreateFailure.js'
import { MarkdownEditor, type MarkdownEditorHandle } from '../features/practices/MarkdownEditor.js'
import {
  CreatePracticeDocument,
  NewPracticeConfigurationDocument,
} from '../graphql/generated/operations.js'
import {
  subjectMatchesCategories,
  useSubjectPreferences,
} from '../features/practices/useSubjectPreferences.js'
import { useAutomaticPractice } from '../features/practices/useAutomaticPractice.js'
import { useUserErrorText } from '../lib/user-errors.js'

/**
 * Create a practice: two ways of starting, one page.
 *
 * **Automatic** takes photographs of the questions, reads the text off them,
 * and has Athena's API write a practice from it. **Manual** is the Markdown
 * flow that was this whole screen, unchanged: a tutor who already writes their
 * practices by hand does the same thing they did before, one click further in.
 *
 * The tabs are a choice about where the text comes from, not two products.
 * Which is why the subject select appears on both and comes from the same
 * catalog query either way — the practice is about the same thing however its
 * questions were typed.
 *
 * A successful automatic run persists its validated draft and opens the
 * canonical practice page. Manual creation still accepts Markdown here, but
 * both paths end in the same Practice and PracticeVersion lifecycle.
 *
 * ── The manual flow ──────────────────────────────────────────────────────
 *
 * The handoff's flow is `Name → Upload/Paste → Parse → Review → Preview →
 * Assign → Publish`. Two of those steps look different here, for reasons that
 * come from the API rather than from the design:
 *
 * The name is the Markdown's `# heading`. V1 authoring is Markdown only
 * (V1.md section 18), and the parser takes the title from the document, so a
 * separate name field would be a second place to disagree about what the
 * practice is called.
 *
 * Parse is the same call as create. `createPractice` parses and, when the
 * document is good, writes a DRAFT. A draft is unpublished and unassigned,
 * which is exactly the "review" state the flow describes, so the next screen
 * is the review and preview step rather than a detour before saving.
 *
 * Subject and level are the one thing here the Markdown does not carry. They
 * are catalog choices rather than authored content, so they are fields — and
 * the options come from the server, because a hard-coded list would be a
 * second opinion about a catalog the API already owns.
 *
 * The `.md` file picker is gone. Pasting is how Markdown arrives now, and the
 * upload area stays for the half of its job that had nothing to do with
 * files: naming the line every parse error is on, and jumping the editor to
 * it. Nothing about the parser, the mutation or the editor changed with it.
 */
type Tab = 'automatic' | 'manual'

export function NewPracticeScreen() {
  const t = useTranslate()
  const userErrorText = useUserErrorText()
  const language = useLanguage()
  usePageTitle(t('practice.create'))
  const navigate = useNavigate()
  const editorRef = useRef<MarkdownEditorHandle>(null)

  const [tab, setTab] = useState<Tab>('automatic')
  const [source, setSource] = useState('')
  const [announcement, setAnnouncement] = useState('')
  const toast = useToast()
  const [createPractice, { loading }] = useMutation(CreatePracticeDocument, {
    update(cache, { data }) {
      if (!data?.createPractice.practice) return
      // Lists may be inactive while creating. Evict every subject/filter variant
      // so returning to Practices fetches the newly created practice too.
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'myPractices' })
    },
  })
  const [state, setState] = useState<MarkdownUploadState>({ status: 'idle' })

  const { data: configuration } = useQuery(NewPracticeConfigurationDocument, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  })
  const [subjectId, setSubjectId] = useState('')
  // Only after a failed submit, so the form does not scold a tutor for fields
  // they have not reached yet.
  const [showRequired, setShowRequired] = useState(false)
  /**
   * A create that never got an answer, beside the button that sent it. Kept
   * per tab, like the refusals, because each tab has its own button.
   */
  const [createFailure, setCreateFailure] = useState<{ tab: Tab; error: unknown }>()

  // Held here, not in the tab, so a look at the other one does not throw the
  // tutor's images away — nor the practice it generated, nor the edits made
  // to it. The Manual tab's document is here for the same reason.
  const automatic = useAutomaticPractice(configuration?.practiceGenerationConfiguration)

  useEffect(() => {
    if (!automatic.createdPracticeId) return
    void navigate({
      to: '/$language/practices/$practiceId',
      params: { language, practiceId: automatic.createdPracticeId },
    }).then(() => toast.show(t('practice.created')))
  }, [automatic.createdPracticeId, language, navigate, t, toast])

  const preferences = useSubjectPreferences()
  const subjectOptions = [
    ...new Map(
      (configuration?.availableSubjects ?? []).map((subject) => [subject.id, subject]),
    ).values(),
  ]
    .filter(
      (subject) =>
        !preferences.hiddenIds.includes(subject.id) &&
        subjectMatchesCategories(subject, preferences.categoryCodes),
    )
    .map((subject) => ({
      value: subject.id,
      label: subject.name,
    }))
  const missingSubject = !subjectOptions.some((option) => option.value === subjectId)

  const errorLines =
    state.status === 'failed'
      ? state.errors.flatMap((error) => (error.line === null ? [] : [error.line]))
      : []

  /**
   * Create a manual practice from the tutor's Markdown document.
   *
   * On success it navigates to the new practice and confirms it there, with a
   * toast: this screen, and anything it could have said, is gone by then.
   * When the server refuses, it returns the localized problems and lets the
   * caller decide where to show them — the two tabs put them in different
   * places. When the request never got an answer, it returns that too.
   */
  const createFrom = useCallback(
    async (
      markdown: string,
      forSubject: string,
    ): Promise<
      | { outcome: 'created' }
      | { outcome: 'refused'; errors: MarkdownParseError[] }
      | { outcome: 'failed'; error: unknown }
    > => {
      let payload
      try {
        payload = (await createPractice({ variables: { markdown, subjectId: forSubject } })).data
          ?.createPractice
      } catch (error) {
        return { outcome: 'failed', error }
      }

      if (payload?.practice) {
        await navigate({
          to: '/$language/practices/$practiceId',
          params: { language, practiceId: payload.practice.id },
        })
        toast.show(t('practice.created'))
        return { outcome: 'created' }
      }

      // Every parse error keeps its line — the editor's gutter and "Fix in
      // editor" both read it — and gains its Spanish or English wording here.
      // The upload area shows a message; which words those are is the app's
      // business, not the design system's.
      return {
        outcome: 'refused',
        errors: payload
          ? payload.userErrors.map((error) => ({
              code: error.code,
              message: userErrorText(error),
              line: error.line,
            }))
          : [{ code: 'UNKNOWN', message: t('practice.unreadable'), line: null }],
      }
    },
    [createPractice, language, navigate, t, toast, userErrorText],
  )

  async function onCreate() {
    if (missingSubject) {
      // The server refuses this too. Saying so here saves a round trip; it is
      // not what makes the rule hold.
      setShowRequired(true)
      setAnnouncement(t('practice.chooseSubject'))
      return
    }

    setState({ status: 'parsing' })
    setAnnouncement(t('practice.parsingAnnouncement'))
    setCreateFailure(undefined)

    const result = await createFrom(source, subjectId)
    if (result.outcome === 'created') return
    if (result.outcome === 'failed') {
      // Back to idle rather than left "parsing", with every line still in the
      // editor. The notice says what happened; its alert is the announcement.
      setState({ status: 'idle' })
      setAnnouncement('')
      setCreateFailure({ tab: 'manual', error: result.error })
      return
    }

    const { errors } = result
    setState({ status: 'failed', errors })
    setAnnouncement(
      errors.length === 1 ? t('count.problemsOne') : t('count.problemsOther', [errors.length]),
    )
  }

  return (
    <PageLayout width="wide">
      {/* Title and mode selection are page navigation, not content inside the
          editor. Each tab can now compose its own workbench without a
          page-sized card around it. */}
      <h2 className="text-h2 font-semibold text-text">{t('practice.newTitle')}</h2>
      {configuration?.availableSubjects.length === 0 ? (
        <FieldMessage>{t('subjectCategories.empty')}</FieldMessage>
      ) : null}

      <Tabs
        label={t('practice.createTabsLabel')}
        items={[
          { value: 'automatic', label: t('practice.automatic.tab') },
          { value: 'manual', label: t('practice.manual.tab') },
        ]}
        value={tab}
        onValueChange={setTab}
      >
        {tab === 'automatic' ? (
          <AutomaticPracticeForm subjectOptions={subjectOptions} automatic={automatic} />
        ) : (
          <div className="flex flex-col gap-6">
            <p className="max-w-measure-max text-body text-text-secondary">
              {t('practice.newSubtitle')}
            </p>

            {/* Configuration and the authored document become parallel only at
                the desktop breakpoint. Narrow document order remains subject,
                optional help, parse feedback, editor, then Create. */}
            <div className="grid min-w-0 gap-6 md:gap-8 desktop:grid-cols-[minmax(16rem,1fr)_minmax(0,2fr)] desktop:items-start">
              <div className="flex min-w-0 flex-col gap-6">
                <div className="grid gap-4 md:grid-cols-2 desktop:grid-cols-1">
                  <Select
                    label={t('practice.fields.subject')}
                    placeholder={t('practice.fields.chooseSubject')}
                    options={subjectOptions}
                    searchable
                    visibleOptionCount={6}
                    value={missingSubject ? '' : subjectId}
                    onValueChange={setSubjectId}
                    reserveMessage
                    {...(showRequired && missingSubject
                      ? { error: t('practice.fields.subjectRequired') }
                      : {})}
                  />
                </div>

                {/* This component continues to own its disclosure state. */}
                <ChatGPTPromptPanel />
              </div>

              <div className="flex min-w-0 flex-col gap-6">
                {/* Parse feedback stays with the document it describes. Idle
                    renders nothing; upload and error behavior are unchanged. */}
                <MarkdownUploadArea
                  state={state}
                  onFixError={(line) => editorRef.current?.focusLine(line)}
                />

                <MarkdownEditor
                  ref={editorRef}
                  value={source}
                  onChange={setSource}
                  errorLines={errorLines}
                />

                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    size="lg"
                    onClick={() => void onCreate()}
                    loading={loading}
                    loadingText={t('common.creating')}
                    disabled={source.trim().length === 0}
                  >
                    {t('practice.create')}
                  </Button>
                  {source.trim().length === 0 ? (
                    <p className="text-body-sm text-text-secondary">{t('practice.pasteToStart')}</p>
                  ) : null}
                </div>
                {createFailure?.tab === 'manual' ? (
                  <CreateFailure error={createFailure.error} />
                ) : null}
              </div>
            </div>
          </div>
        )}
      </Tabs>

      <LiveRegion message={announcement} />
    </PageLayout>
  )
}
